/**
 * meta-atc-bridge.js
 * --------------------------------------------------------------------------
 * Purpose
 *   Turbo 7.0.0 submits the product form natively (document-level POST to
 *   /cart/add), so the page unloads before Shopify's Web Pixels layer can
 *   emit `product_added_to_cart`. The Facebook & Instagram app's managed
 *   pixel subscribes to that event, so no AddToCart ever reaches Meta.
 *
 *   This bridge converts the native submit into a Cart Ajax API call
 *   (/cart/add.js), which Shopify DOES instrument, then performs the same
 *   redirect the shopper expected. Net effect: identical UX, event fires.
 *
 *   It deliberately does NOT call fbq() directly. The managed pixel runs in
 *   a sandbox; a second fbq call would create an unmanaged duplicate that
 *   Meta cannot deduplicate against the Conversions API stream.
 *
 * Safety
 *   - Falls back to the native submit on ANY failure or timeout.
 *   - Ignores "Buy it now" / dynamic checkout submissions.
 *   - Ignores cart-page update forms.
 *   - No-ops if another script already handled the submit.
 *
 * Install
 *   1. Online Store > Themes > ... > Edit code
 *   2. Assets > Add a new asset > create `meta-atc-bridge.js`, paste this in
 *   3. Layout > theme.liquid, immediately before </body>, add:
 *        {{ 'meta-atc-bridge.js' | asset_url | script_tag }}
 *   4. Save. Duplicate your theme first — Turbo updates overwrite theme.liquid.
 */
(function () {
  'use strict';

  var CONFIG = {
    // Where to send the shopper after a successful add. Set to null to stay put.
    redirectTo: '/cart',

    // Give up on the Ajax call after this many ms and let the native submit run.
    timeoutMs: 8000,

    // Set true to stop other submit handlers. Only needed if you discover a
    // second script also adding to cart (symptom: quantity increments by 2).
    stopOtherHandlers: false,

    // Set true to log decisions to the console while testing.
    debug: false
  };

  function log() {
    if (!CONFIG.debug) return;
    var args = ['[meta-atc-bridge]'].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }

  // Matches /cart/add and locale-prefixed variants like /en-ca/cart/add
  function isAddToCartForm(form) {
    var action = form.getAttribute('action') || '';
    return /\/cart\/add(\?|$)/.test(action.split('#')[0]);
  }

  // "Buy it now" and Shop Pay express buttons must reach checkout natively.
  function isExpressCheckout(submitter) {
    if (!submitter) return false;
    if (submitter.name === 'checkout') return true;
    if (submitter.hasAttribute('data-testid')) return true;
    return !!submitter.closest('.shopify-payment-button, [data-shopify="payment-button"]');
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;

      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.metaAtcBridged === 'submitting') return; // our own fallback
      if (!isAddToCartForm(form)) return;

      var submitter = event.submitter || form.querySelector('[type="submit"]');
      if (isExpressCheckout(submitter)) {
        log('express checkout — passing through');
        return;
      }

      if (event.defaultPrevented) {
        log('already handled by another script — standing down');
        return;
      }

      log('intercepting native submit');
      event.preventDefault();
      if (CONFIG.stopOtherHandlers) event.stopImmediatePropagation();

      var action = form.getAttribute('action') || '/cart/add';
      var ajaxUrl = action.split('?')[0].replace(/\/cart\/add$/, '/cart/add.js');
      var body = new FormData(form);
      var settled = false;

      if (submitter) submitter.setAttribute('aria-busy', 'true');

      function nativeFallback(reason) {
        if (settled) return;
        settled = true;
        log('falling back to native submit:', reason);
        form.dataset.metaAtcBridged = 'submitting';
        HTMLFormElement.prototype.submit.call(form);
      }

      function succeed() {
        if (settled) return;
        settled = true;
        log('added via Ajax API — Shopify will emit product_added_to_cart');
        if (CONFIG.redirectTo) {
          window.location.href = CONFIG.redirectTo;
        } else if (submitter) {
          submitter.removeAttribute('aria-busy');
        }
      }

      var timer = window.setTimeout(function () {
        nativeFallback('timeout');
      }, CONFIG.timeoutMs);

      fetch(ajaxUrl, {
        method: 'POST',
        body: body,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(function (response) {
          window.clearTimeout(timer);
          if (!response.ok) {
            // 422 = genuine merchandising error (sold out, min quantity, etc).
            // Let the native submit run so Turbo renders its own error state.
            return nativeFallback('HTTP ' + response.status);
          }
          return response.json().then(succeed, succeed);
        })
        .catch(function (error) {
          window.clearTimeout(timer);
          nativeFallback(error && error.message);
        });
    },
    true // capture phase — run before Turbo's own handlers
  );

  log('ready');
})();